import { createFormControl } from 'react-hook-form';
import { CreateApplicationFormDataValues } from './schema';
import { useCallback, useMemo } from 'react';
import { AppToaster } from '../../components/AppToaster';
import { useCreateCustomer } from '@axo/shared/data-access/hooks/mutations/useCreateCustomer';
import { useCreatePerson } from '@axo/shared/data-access/hooks/mutations/useCreatePerson';
import { useCreateApplication } from '@axo/shared/data-access/hooks/mutations/useCreateApplication';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { getBrandForGeneralConsent } from '@axo/config/util/get-brand-for-general-consent';
import { isBrand } from '@axo/config/@types/Brand.types';
import { getTermsAndConditions } from '@axo/shared/data-access/hooks/queries/useTermsAndConditions';
import { useCreateConsent } from '@axo/shared/data-access/hooks/mutations/useCreateConsent';
import { useGetPersonByS2ID } from '@axo/shared/data-access/hooks/services/puzzel/useGetPersonByS2ID';

export const useCreateApplicationFormOnSubmitFactory = ({
  formContext,
}: {
  formContext: ReturnType<
    typeof createFormControl<CreateApplicationFormDataValues>
  >;
}) => {
  const { mutateAsync: createCustomer } = useCreateCustomer();
  const { mutateAsync: createPerson } = useCreatePerson();
  const { mutateAsync: createApplication } = useCreateApplication();
  const { mutateAsync: createConsent } = useCreateConsent();

  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const campaignName = searchParams.get('utm_name') ?? 'ace';
  const campaignSource = searchParams.get('utm_source') ?? 'ace';
  const campaignContent = searchParams.get('utm_content') ?? undefined;
  const s2_id = searchParams.get('s2_id');
  const { data: s2PersonData } = useGetPersonByS2ID(s2_id ?? undefined);

  const onSubmit = useCallback(async () => {
    try {
      // Snapshot values to avoid race conditions during async operations
      const values = structuredClone(formContext.getValues());

      const customerRes = await createCustomer({
        MobilePhoneCountryCode: values.Customer.MobilePhone.CountryCode,
        MobilePhoneNumber: values.Customer.MobilePhone.Number,
        Email: values.Customer.Email,
        MarketCountry: values.Application.MarketCountry,
      });

      if (!customerRes.ID) throw new Error('Failed to create customer');

      const personRes = await createPerson({
        MarketCountry: values.Application.MarketCountry,
        SocialSecurityNumber: values.Person.SocialSecurityNumber,
      });

      if (!personRes.ID) throw new Error('Failed to create person');

      if (!isBrand(values.Application.Meta.Brand))
        throw new Error('Invalid brand');

      const generalConsentBrand = getBrandForGeneralConsent(
        values.Application.Meta.Brand,
      );

      const termsAndConditions = await getTermsAndConditions(
        values.Application.Meta.Brand,
      );

      const generalTermsVersion = termsAndConditions.terms.find(
        (terms) => terms.terms.tc_type === 'GENERAL_TERMS',
      )?.version;

      if (!generalTermsVersion)
        throw new Error('Failed to fetch general terms and conditons');

      await createConsent({
        Country: values.Application.MarketCountry,
        OriginBrandID: generalConsentBrand,
        ExternalVersion: generalTermsVersion,
        CustomerID: customerRes.ID,
        Lang: 'N/A',
        OriginAction: 'ace-create-application-form',
        ConfigType: 'GENERAL_TERMS',
      });

      const loanAppRes = await createApplication({
        MarketCountry: values.Application.MarketCountry,
        AppliedAmount: values.Application.AppliedAmount,
        LoanDuration: values.Application.LoanDuration,
        CustomerID: customerRes.ID,
        PersonID: personRes.ID,
        Meta: {
          Brand: values.Application.Meta.Brand,
        },
        Channels: [
          {
            CampaignName: campaignName,
            CampaignMedium: 'ace',
            CampaignSource: campaignSource,
            CampaignContent: campaignContent,
            InternalS2BucketID: s2PersonData?.Distribution?.Bucket ?? undefined,
            InternalS2ProjectID:
              s2PersonData?.Distribution?.Project ?? undefined,
          },
        ],
      });

      navigate(`/application/${loanAppRes.ID}`);
    } catch (e) {
      if (e instanceof Error) {
        AppToaster.then((toaster) =>
          toaster.show({
            intent: 'danger',
            message: e.message,
          }),
        );
      }
    } finally {
      formContext.reset();
      formContext.trigger();
    }
  }, [
    formContext,
    createPerson,
    createCustomer,
    createConsent,
    createApplication,
    navigate,
    campaignName,
    campaignSource,
    campaignContent,
    s2PersonData?.Distribution?.Bucket,
    s2PersonData?.Distribution?.Project,
  ]);

  return useMemo(
    () => formContext.handleSubmit(onSubmit, onSubmit),
    [formContext, onSubmit],
  );
};
